import { sectionRenderer } from '@theme/section-renderer';

/**
 * GVS default collection mobile commerce controls.
 *
 * Native facets remain authoritative for filter URL generation and AJAX
 * rendering. This module owns the mobile filter/sort presentation, pointer vs
 * keyboard focus handling, sort confirmation, focus restoration and browser
 * history rendering for the default GVS collection.
 */
const stateKey = '__gvsCollectionCommerceBound';

if (!window[stateKey]) {
  window[stateKey] = true;

  /* -----------------------------------------------------------------------
   * Input modality
   * -----------------------------------------------------------------------
   * Safari/iOS and some Chromium touch paths can continue to match
   * :focus-visible after a summary is tapped. Track the customer's actual
   * modality so pointer/touch interaction never leaves a keyboard outline,
   * while Tab/keyboard users retain a strong focus indicator.
   */
  const modalityAttribute = 'data-gvs-input-modality';

  /** @param {'pointer' | 'keyboard'} modality */
  const setPageInputModality = (modality) => {
    document.documentElement.setAttribute(modalityAttribute, modality);
  };

  if (!document.documentElement.hasAttribute(modalityAttribute)) {
    setPageInputModality('pointer');
  }

  document.addEventListener(
    'pointerdown',
    () => setPageInputModality('pointer'),
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (
        event.key === 'Tab' ||
        event.key === 'Enter' ||
        event.key === ' ' ||
        event.key.startsWith('Arrow') ||
        event.key === 'Home' ||
        event.key === 'End'
      ) {
        setPageInputModality('keyboard');
      }
    },
    true
  );

  document.addEventListener(
    'pointerup',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const summary = target?.closest('.gvs-default-collection .facets__summary');
      if (!(summary instanceof HTMLElement)) return;

      requestAnimationFrame(() => {
        if (document.activeElement === summary) summary.blur();
      });
    },
    true
  );

  /** @param {string | null | undefined} value */
  const escapeSelectorValue = (value) => {
    if (!value) return '';
    return window.CSS?.escape ? CSS.escape(value) : value.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  };

  /** @param {string | null | undefined} sectionId */
  const getCollection = (sectionId) => {
    const escaped = escapeSelectorValue(sectionId);
    const selector = escaped
      ? `results-list.gvs-default-collection[section-id="${escaped}"]`
      : 'results-list.gvs-default-collection[section-id]';
    const collection = document.querySelector(selector);
    return collection instanceof HTMLElement ? collection : null;
  };

  /**
   * Keep every visible result-count surface in lockstep with the authoritative
   * Shopify filtered count carried by the freshly rendered results-list. This
   * is deliberately explicit because the section morph can preserve existing
   * text nodes while replacing the product grid and facet state.
   * @param {string | null | undefined} sectionId
   */
  const syncResultCounts = (sectionId) => {
    const collection = getCollection(sectionId);
    if (!collection) return;

    const count = Number.parseInt(collection.dataset.gvsResultsCount || '', 10);
    if (!Number.isFinite(count) || count < 0) return;

    const noun = count === 1 ? 'product' : 'products';

    for (const node of collection.querySelectorAll('[data-gvs-results-count]')) {
      node.textContent = String(count);
    }

    for (const node of collection.querySelectorAll('[data-gvs-results-noun]')) {
      node.textContent = noun;
    }

    for (const action of collection.querySelectorAll('[data-gvs-results-action]')) {
      if (!(action instanceof HTMLButtonElement)) continue;

      const zeroLabel = action.dataset.gvsZeroResultsLabel || 'No matching products';
      if (count === 0) {
        action.disabled = true;
        action.setAttribute('aria-disabled', 'true');
        action.classList.remove('button-primary');
        action.classList.add('facets__see-results--empty');
        action.textContent = zeroLabel;
      } else {
        action.disabled = false;
        action.removeAttribute('aria-disabled');
        action.classList.add('button-primary');
        action.classList.remove('facets__see-results--empty');
        action.innerHTML = `View <span data-gvs-results-count>${count}</span> <span data-gvs-results-noun>${noun}</span>`;
      }
    }
  };

  /** @param {string | null | undefined} sectionId @param {'filter' | 'sort'} type */
  const getTrigger = (sectionId, type) => {
    const collection = getCollection(sectionId);
    const selector = type === 'filter' ? '[data-gvs-filter-trigger]' : '[data-gvs-sort-trigger]';
    const trigger = collection?.querySelector(selector);
    return trigger instanceof HTMLElement ? trigger : null;
  };

  /** @param {Element | null} element */
  const getDialogComponent = (element) => {
    const component = element?.closest('dialog-component');
    return component instanceof HTMLElement ? component : null;
  };

  /** @param {Element | null} element */
  const closeDialog = async (element) => {
    const component = getDialogComponent(element);
    if (component && typeof component.closeDialog === 'function') {
      await component.closeDialog();
    }
  };

  /** @param {string} sectionId @param {boolean} isBusy */
  const setBusy = (sectionId, isBusy) => {
    const collection = getCollection(sectionId);
    if (!collection) return;

    if (isBusy) collection.setAttribute('aria-busy', 'true');
    else collection.removeAttribute('aria-busy');

    for (const control of collection.querySelectorAll('[data-gvs-filter-trigger], [data-gvs-sort-trigger]')) {
      if (!(control instanceof HTMLElement)) continue;
      if (isBusy) control.setAttribute('aria-busy', 'true');
      else control.removeAttribute('aria-busy');
    }
  };

  /** @param {HTMLDialogElement} dialog */
  const getInputModality = (dialog) => (dialog.dataset.inputModality === 'keyboard' ? 'keyboard' : 'pointer');

  /**
   * Pointer-opened sheets should not leave a keyboard-style ring on the close
   * control or restored toolbar trigger. Keyboard users retain normal focus.
   * @param {HTMLElement | null} element
   */
  const clearPointerFocus = (element) => {
    if (!element) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (document.activeElement === element) element.blur();
      });
    });
  };

  /** @param {string} sectionId */
  const renderForHistory = async (sectionId) => {
    const scrollTop = window.scrollY;
    setBusy(sectionId, true);

    try {
      await sectionRenderer.renderSection(sectionId, { cache: false });
      syncResultCounts(sectionId);
      requestAnimationFrame(() => window.scrollTo({ top: scrollTop, behavior: 'instant' }));
    } catch (error) {
      window.location.reload();
    } finally {
      setBusy(sectionId, false);
    }
  };

  /** @param {HTMLDialogElement} dialog */
  const updateSortSelection = (dialog) => {
    const selected = dialog.querySelector('[data-gvs-sort-option]:checked');
    for (const option of dialog.querySelectorAll('.gvs-mobile-sort-option')) {
      option.classList.toggle('is-selected', option.contains(selected));
    }
  };

  /** @param {HTMLDialogElement} sortDialog */
  const applyMobileSort = async (sortDialog) => {
    const selected = sortDialog.querySelector('[data-gvs-sort-option]:checked');
    const sectionId = sortDialog.getAttribute('data-section-id');
    const collection = getCollection(sectionId);

    if (!(selected instanceof HTMLInputElement) || !sectionId || !collection) return;

    const url = new URL(window.location.href);
    const currentSort = sortDialog.getAttribute('data-current-sort') || url.searchParams.get('sort_by');
    const pageScrollTop = window.scrollY;
    const modality = getInputModality(sortDialog);

    // Complete the native dialog close before the collection section morphs.
    // This prevents a detached closing header/close button remaining in the
    // browser top layer after sorting.
    await closeDialog(sortDialog);

    if (currentSort === selected.value) {
      if (modality === 'pointer') clearPointerFocus(getTrigger(sectionId, 'sort'));
      return;
    }

    url.searchParams.set('sort_by', selected.value);
    url.searchParams.delete('page');
    history.pushState({ urlParameters: url.searchParams.toString() }, '', url.toString());
    setBusy(sectionId, true);

    try {
      await sectionRenderer.renderSection(sectionId, { cache: false });
      syncResultCounts(sectionId);
      requestAnimationFrame(() => {
        window.scrollTo({ top: pageScrollTop, behavior: 'instant' });
        const trigger = getTrigger(sectionId, 'sort');
        if (modality === 'keyboard') trigger?.focus({ preventScroll: true });
        else clearPointerFocus(trigger);
      });
    } catch (error) {
      window.location.assign(url.toString());
    } finally {
      setBusy(sectionId, false);
    }
  };

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-gvs-sort-option]')) return;
    const dialog = input.closest('[data-gvs-sort-dialog]');
    if (dialog instanceof HTMLDialogElement) updateSortSelection(dialog);
  });

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-gvs-sort-apply]') : null;
    if (!(button instanceof HTMLButtonElement)) return;
    const dialog = button.closest('[data-gvs-sort-dialog]');
    if (dialog instanceof HTMLDialogElement) void applyMobileSort(dialog);
  });

  document.addEventListener(
    'dialog:open',
    (event) => {
      const component = event.target;
      if (!(component instanceof Element)) return;

      const sortDialog = component.querySelector('[data-gvs-sort-dialog]');
      if (sortDialog instanceof HTMLDialogElement) {
        const sectionId = sortDialog.getAttribute('data-section-id');
        getTrigger(sectionId, 'sort')?.setAttribute('aria-expanded', 'true');
        updateSortSelection(sortDialog);

        requestAnimationFrame(() => {
          if (getInputModality(sortDialog) === 'keyboard') {
            const selected = sortDialog.querySelector('[data-gvs-sort-option]:checked');
            if (selected instanceof HTMLElement) selected.focus({ preventScroll: true });
          } else {
            sortDialog.focus({ preventScroll: true });
          }
        });
        return;
      }

      const filterDialog = component.querySelector('[data-gvs-filter-dialog]');
      if (filterDialog instanceof HTMLDialogElement) {
        const sectionId = filterDialog.getAttribute('data-section-id');
        getTrigger(sectionId, 'filter')?.setAttribute('aria-expanded', 'true');
        if (sectionId) restoreFacetState(sectionId);

        requestAnimationFrame(() => {
          if (getInputModality(filterDialog) === 'keyboard') {
            const closeButton = filterDialog.querySelector('.facets-drawer__close');
            if (closeButton instanceof HTMLElement) closeButton.focus({ preventScroll: true });
          } else {
            filterDialog.focus({ preventScroll: true });
          }
        });
      }
    },
    true
  );

  document.addEventListener(
    'dialog:close',
    (event) => {
      const component = event.target;
      if (!(component instanceof Element)) return;

      const sortDialog = component.querySelector('[data-gvs-sort-dialog]');
      if (sortDialog instanceof HTMLDialogElement) {
        const sectionId = sortDialog.getAttribute('data-section-id');
        const trigger = getTrigger(sectionId, 'sort');
        trigger?.setAttribute('aria-expanded', 'false');
        if (getInputModality(sortDialog) === 'pointer') clearPointerFocus(trigger);
      }

      const filterDialog = component.querySelector('[data-gvs-filter-dialog]');
      if (filterDialog instanceof HTMLDialogElement) {
        const sectionId = filterDialog.getAttribute('data-section-id');
        const trigger = getTrigger(sectionId, 'filter');
        trigger?.setAttribute('aria-expanded', 'false');
        if (getInputModality(filterDialog) === 'pointer') clearPointerFocus(trigger);
      }
    },
    true
  );

  document.addEventListener('gvs:collection-render-start', (event) => {
    const sectionId = event.detail?.sectionId;
    if (typeof sectionId === 'string') setBusy(sectionId, true);
  });

  document.addEventListener('gvs:collection-render-end', (event) => {
    const sectionId = event.detail?.sectionId;
    if (typeof sectionId === 'string') {
      setBusy(sectionId, false);
      syncResultCounts(sectionId);
      requestAnimationFrame(() => requestAnimationFrame(() => restoreFacetState(sectionId)));
    }
  });

  for (const collection of document.querySelectorAll('results-list.gvs-default-collection[section-id]')) {
    if (collection instanceof HTMLElement) syncResultCounts(collection.getAttribute('section-id'));
  }

  /* -----------------------------------------------------------------------
   * Facet accordion state
   * -----------------------------------------------------------------------
   * A filtered catalogue can contain many active groups. Opening every active
   * group after each AJAX update recreates the original density problem. Keep
   * one group open per context and restore only the group the customer was
   * actually using.
   */
  const openFacetState = new Map();

  /** @param {HTMLDetailsElement} details */
  const getFacetContext = (details) =>
    details.closest('.facets-block-wrapper--drawer') ? 'drawer' : 'desktop';

  /** @param {HTMLDetailsElement} details */
  const getFacetName = (details) => {
    const accordion = details.closest('accordion-custom[data-filter-param-name]');
    return accordion instanceof HTMLElement ? accordion.dataset.filterParamName || '' : '';
  };

  /** @param {HTMLDetailsElement} details */
  const getFacetSectionId = (details) => {
    const collection = details.closest('results-list.gvs-default-collection[section-id]');
    return collection instanceof HTMLElement ? collection.getAttribute('section-id') || '' : '';
  };

  /** @param {string} sectionId @param {'desktop' | 'drawer'} context */
  const facetStateKey = (sectionId, context) => `${sectionId}:${context}`;

  /** @param {HTMLDetailsElement} details */
  const rememberFacet = (details) => {
    const sectionId = getFacetSectionId(details);
    const name = getFacetName(details);
    if (!sectionId || !name) return;
    openFacetState.set(facetStateKey(sectionId, getFacetContext(details)), name);
  };

  /** @param {HTMLDetailsElement} details */
  const closeSiblingFacets = (details) => {
    const wrapper = details.closest('.facets__filters-wrapper');
    if (!(wrapper instanceof HTMLElement)) return;

    for (const sibling of wrapper.querySelectorAll(':scope > accordion-custom > details[open]')) {
      if (sibling instanceof HTMLDetailsElement && sibling !== details) sibling.open = false;
    }
  };

  /** @param {string} sectionId */
  const restoreFacetState = (sectionId) => {
    const collection = getCollection(sectionId);
    if (!collection) return;

    // Clearing all filters returns to the server-rendered default disclosure.
    if (!collection.querySelector('.gvs-collection-active-filters')) {
      openFacetState.delete(facetStateKey(sectionId, 'desktop'));
      openFacetState.delete(facetStateKey(sectionId, 'drawer'));
      return;
    }

    for (const context of ['desktop', 'drawer']) {
      const name = openFacetState.get(facetStateKey(sectionId, context));
      if (!name) continue;

      const containerSelector =
        context === 'drawer'
          ? '.facets-block-wrapper--drawer .facets__filters-wrapper'
          : '.facets-block-wrapper--vertical:not(.facets-block-wrapper--drawer) .facets__filters-wrapper';
      const wrapper = collection.querySelector(containerSelector);
      if (!(wrapper instanceof HTMLElement)) continue;

      const escapedName = escapeSelectorValue(name);
      const target = wrapper.querySelector(
        `accordion-custom[data-filter-param-name="${escapedName}"] > details`
      );

      if (!(target instanceof HTMLDetailsElement)) {
        openFacetState.delete(facetStateKey(sectionId, context));
        continue;
      }

      for (const details of wrapper.querySelectorAll(':scope > accordion-custom > details[open]')) {
        if (details instanceof HTMLDetailsElement && details !== target) details.open = false;
      }
      target.open = true;
    }
  };

  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.matches('.facets__panel')) return;
      if (!details.closest('results-list.gvs-default-collection')) return;

      const sectionId = getFacetSectionId(details);
      const name = getFacetName(details);
      if (!sectionId || !name) return;
      const key = facetStateKey(sectionId, getFacetContext(details));

      if (details.open) {
        closeSiblingFacets(details);
        openFacetState.set(key, name);
      } else if (openFacetState.get(key) === name) {
        openFacetState.delete(key);
      }
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      const details = target.closest('details.facets__panel');
      if (details instanceof HTMLDetailsElement) rememberFacet(details);
    },
    true
  );

  window.addEventListener('resize', () => {
    if (window.innerWidth < 750) return;

    for (const dialog of document.querySelectorAll('[data-gvs-sort-dialog][open], [data-gvs-filter-dialog][open]')) {
      if (dialog instanceof HTMLDialogElement) void closeDialog(dialog);
    }
  });

  window.addEventListener('popstate', () => {
    const collection = getCollection();
    const sectionId = collection?.getAttribute('section-id');
    if (sectionId) void renderForHistory(sectionId);
  });
}
